import lodash from 'lodash';
import type { ComponentPropsWithoutRef } from 'react';
import React, { useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import type { TextFieldProps } from '@mui/material/TextField';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import { tss } from 'tss-react/mui';

import type { businessTypes } from 'app-components/flux/business';
import { businessSelectors } from 'app-components/flux/business';

import UsageAndEntityIcon from 'components/UsageAndEntityIcon';
import { getMenuItems } from 'components/UsageAndEntitySelector';

import type { InputProps, SelectProps } from '@mui/material';
import { Stack, Typography } from '@mui/material';
import ButtonBase from '@mui/material/ButtonBase';
import type { SlotCommonProps } from '@mui/material/utils/types';
import type { Business } from 'app-components/flux/business/businessTypes';

const useStyles = tss.withParams().create(({ size }) => {
  // When creating a select with a button, the size of the input element is different than the size of
  // a normal select input element. This causes the overall select height to be different than other selects.
  // So, to fix this issue, we will adjust padding so the overall select wil lbe the same height.
  // This is fragile and may break if the size of a text based select changes (which also depends on font size)
  // or the size of the button changes.
  // Values Used:
  //   - 56px: The height of a standard text based select
  //   - 48px: The height of a small text based select
  //   - 40px: The height of an button
  //
  let pt = '8px';
  let pb = '8px';
  if (size && size === 'small') {
    pt = '4px';
    pb = '4px';
  }

  return {
    buttonRoot: {
      textAlign: 'center',
    },
    select: {
      paddingTop: pt,
      paddingBottom: pb,
    },
  };
});

export type UsageSelectPropTypes = {
  disabled?: boolean;
  iconOnly?: boolean;
  onChange?: (e: any, value: string) => void;
  showPersonal?: boolean;
  size?: string;
  value?: string | null;
  nameAndIcon?: boolean;
  className?: string;
  fallback?: string;
  label?: string;
  cellId?: string;
  slotProps?: {
    root?: ComponentPropsWithoutRef<'div'>;
    SelectProps?: SelectProps;
    InputProps?: InputProps;
  } & SlotCommonProps &
    TextFieldProps;
};

// When setting the usage-value, if no businessId is set then we want to send an unset value and let
// the usage select control setup the correct default value. Once user has set, then businessId will
// always exist (even if it is set to '0' to represent PERSONAL)
type KnownBizId = Extract<businessTypes.Business['id'], string>;
const getUsageValue = (usageBusinessId?: '0' | KnownBizId | null): 'PERSONAL' | KnownBizId | null => {
  if (!usageBusinessId) return null;
  return usageBusinessId !== '0' ? usageBusinessId : 'PERSONAL';
};

const UsageSelect = React.memo((props: UsageSelectPropTypes) => {
  const {
    disabled,
    iconOnly,
    onChange,
    value: val,
    showPersonal = true,
    size = 'medium',
    slotProps,
    nameAndIcon = false,
    className,
    fallback = 'PERSONAL',
    label = '',
    cellId,
  } = props;
  const { classes } = useStyles({ size });

  const menuRef = useRef<HTMLDivElement>(null);
  const [menuAnchor, setMenuAnchor] = useState<HTMLDivElement | null>(null);

  const businessesById = useSelector(businessSelectors.getResourcesById);

  let business: Business | undefined;
  let usageIcon: 'PERSONAL' | 'BUSINESS' | 'SPLIT' | NonNullable<businessTypes.Business['taxGroup']>;

  // Note: Value is generally a business or 'PERSONAL'. If disabled, and iconOnly is true, then value can be any valid usageIcon.
  const value = getUsageValue(val);

  if (val === 'SPLIT') {
    usageIcon = 'SPLIT';
  } else if (value && value !== 'PERSONAL') {
    business = businessesById.get(value);
    usageIcon = business?.taxGroup || 'BUSINESS';
  } else {
    usageIcon = 'PERSONAL';
  }

  const openMenu = () => setMenuAnchor(menuRef.current);
  const closeMenu = () => setMenuAnchor(null);

  const onItemClick = (event: any, newValue: string) => {
    onChange?.(event, newValue);
    closeMenu();
  };

  const handleChange = (event: any) => {
    onChange?.(event, event?.target?.value);
  };

  const renderIconOnly = () => (
    <Tooltip title={business?.name ?? 'Personal'}>
      <span>
        <IconButton disabled={disabled}>
          <UsageAndEntityIcon color={business?.color} size='small' usageIcon={usageIcon} />
        </IconButton>
      </span>
    </Tooltip>
  );

  const renderNameAndIcon = () => (
    <Stack direction='row' alignItems='center' pt={label.length > 0 ? 1 : 0} mb={label.length > 0 ? -1 : 0}>
      <IconButton disabled={disabled} sx={{ pr: 2 }}>
        <UsageAndEntityIcon color={business?.color} size='small' usageIcon={usageIcon} />
      </IconButton>
      <Typography>{business?.name ?? 'Personal'}</Typography>
    </Stack>
  );

  const { root: rootProps, SelectProps, ...otherSlotProps } = slotProps || {};

  return iconOnly ? (
    <div
      role='presentation'
      data-testid='usage-select-icon-only'
      className={classes.buttonRoot}
      {...(rootProps ?? {})}
      ref={menuRef}
    >
      <Tooltip title={business?.name ?? 'Personal'}>
        <span>
          <ButtonBase id={cellId} disabled={disabled} tabIndex={disabled ? -1 : 0} onClick={openMenu}>
            <UsageAndEntityIcon color={business?.color} size='xsmall' usageIcon={usageIcon} />
          </ButtonBase>
        </span>
      </Tooltip>
      {menuAnchor && (
        <Menu id='IgnoreMenu-menu' anchorEl={menuAnchor} keepMounted open onClose={closeMenu}>
          {getMenuItems(businessesById, {
            onClick: onItemClick,
            showPersonal,
          })}
        </Menu>
      )}
    </div>
  ) : (
    <TextField
      disabled={disabled}
      hiddenLabel={label.length === 0}
      label={label}
      id='usage-field'
      onChange={handleChange}
      select
      sx={{
        '& .MuiSelect-select': {
          textOverflow: 'clip',
        },
        '& .MuiInputBase-root': {
          height: 58,
        },
      }}
      SelectProps={lodash.merge(
        { classes: { select: classes.select }, renderValue: nameAndIcon ? renderNameAndIcon : renderIconOnly },
        SelectProps,
      )}
      value={value || fallback}
      variant='filled'
      className={className}
      {...(otherSlotProps ?? {})}
    >
      {getMenuItems(businessesById, {
        onClick: undefined,
        showPersonal,
      })}
    </TextField>
  );
});

UsageSelect.displayName = 'UsageSelect';

export default UsageSelect;
